// ============================================================================
// ContractsTab — onglet CONTRATS de l'ecran Admin (AD-E : referentiel de modeles)
// ============================================================================
// Ce onglet administre le CATALOGUE d'entreprise de gabarits de contrat (nom +
// corps a jetons de fusion) — le REFERENTIEL, PAS l'emission d'un contrat a un
// employe (celle-ci vit cote fiche employe, par cible). DataTable @gs/ui + dialog
// creer/editer (ContractTemplateDialog, GsWizardDialog une etape) + suppression
// via ConfirmDialog (destructif -> btnDanger).
//
// [!] M-163 : supprimer un gabarit n'affecte AUCUN contrat deja emis (le corps
// rendu est fige a l'emission) — le message de confirmation le rappelle.

import { useState } from 'react';
import { FilePlus, FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { DataTable, EmptyState, SkeletonCard, QueryError, ConfirmDialog, GS_COMPONENTS, type Column } from '@gs/ui';
import { useTranslation } from '../../i18n';
import {
  useCreateContractTemplate, useUpdateContractTemplate, useDeleteContractTemplate,
  adminContractErrorCode,
  type AdminContractTemplate, type AdminContractTemplatesResponse, type ContractTemplatePayload,
} from '../../hooks/useAdminContractTemplates';
import { SectionHeader } from './SectionHeader';
import { ContractTemplateDialog } from './ContractTemplateDialog';

// `template` est FIGE au moment du clic (objet stable — meme motif que MembersTab).
type DialogState =
  | { mode: 'create' }
  | { mode: 'edit'; template: AdminContractTemplate }
  | null;

/** Extrait compact du corps pour la liste (le corps complet vit dans l'editeur). */
function excerpt(body: string): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length > 90 ? `${flat.slice(0, 90)}…` : flat;
}

export function ContractsTab({
  isLoading,
  isError,
  data,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  data: AdminContractTemplatesResponse | undefined;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const createTpl = useCreateContractTemplate();
  const updateTpl = useUpdateContractTemplate();
  const deleteTpl = useDeleteContractTemplate();

  const [dialog, setDialog] = useState<DialogState>(null);
  const [deleteFor, setDeleteFor] = useState<AdminContractTemplate | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (isError || !data) {
    return <QueryError message={t('gest:admin.contracts.loadError')} onRetry={onRetry} />;
  }

  const pending = createTpl.isPending || updateTpl.isPending;

  const done = {
    onSuccess: () => setDialog(null),
    onError: (err: unknown) => {
      setDialog(null);
      setErrorCode(adminContractErrorCode(err));
    },
  };

  function submit(payload: ContractTemplatePayload) {
    setErrorCode(null);
    if (dialog?.mode === 'edit') {
      updateTpl.mutate({ id: dialog.template.id, payload }, done);
    } else {
      createTpl.mutate(payload, done);
    }
  }

  function confirmDelete() {
    if (!deleteFor) return;
    setErrorCode(null);
    deleteTpl.mutate({ id: deleteFor.id }, {
      onSuccess: () => setDeleteFor(null),
      onError: (err: unknown) => {
        setDeleteFor(null);
        setErrorCode(adminContractErrorCode(err));
      },
    });
  }

  const columns: Column<AdminContractTemplate>[] = [
    {
      key: 'name',
      header: t('gest:admin.contracts.columns.name'),
      render: (tpl) => (
        <div className="flex min-w-0 items-center gap-2">
          <FileText size={16} aria-hidden="true" className="shrink-0 text-slate-400 dark:text-slate-500" />
          <p className="truncate font-semibold text-slate-900 dark:text-slate-100">{tpl.name}</p>
        </div>
      ),
    },
    {
      key: 'excerpt',
      header: t('gest:admin.contracts.columns.excerpt'),
      render: (tpl) =>
        tpl.body.trim() ? (
          <span className="text-slate-500 dark:text-slate-400">{excerpt(tpl.body)}</span>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">{'—'}</span>
        ),
    },
    {
      key: 'actions',
      header: t('gest:admin.contracts.columns.actions'),
      className: 'text-right',
      render: (tpl) => (
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            aria-label={t('gest:admin.contracts.edit', { name: tpl.name })}
            onClick={(e) => {
              e.stopPropagation();
              setErrorCode(null);
              setDialog({ mode: 'edit', template: tpl });
            }}
            className="inline-flex h-8 w-8 touch-manipulation items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            <Pencil size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label={t('gest:admin.contracts.delete.action', { name: tpl.name })}
            onClick={(e) => {
              e.stopPropagation();
              setErrorCode(null);
              setDeleteFor(tpl);
            }}
            className="inline-flex h-8 w-8 touch-manipulation items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:text-slate-400 dark:hover:bg-red-950 dark:hover:text-red-400"
          >
            <Trash2 size={16} aria-hidden="true" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title={t('gest:admin.contracts.title')}
        description={t('gest:admin.contracts.description')}
      >
        <span className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.contracts.count', { n: data.templates.length })}
        </span>
        <button
          type="button"
          onClick={() => { setErrorCode(null); setDialog({ mode: 'create' }); }}
          className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-1.5`}
        >
          <Plus size={14} aria-hidden="true" />
          {t('gest:admin.contracts.create')}
        </button>
      </SectionHeader>

      {errorCode ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">
          {t(`gest:admin.contracts.errors.${errorCode}`)}
        </p>
      ) : null}

      {data.templates.length === 0 ? (
        <EmptyState
          icon={<FilePlus size={48} aria-hidden="true" />}
          title={t('gest:admin.contracts.empty')}
          description={t('gest:admin.contracts.emptyHint')}
        />
      ) : (
        <div className={`${GS_COMPONENTS.card} overflow-hidden p-0`}>
          <DataTable
            columns={columns}
            data={data.templates}
            rowKey={(tpl) => tpl.id}
            onRowClick={(tpl) => {
              setErrorCode(null);
              setDialog({ mode: 'edit', template: tpl });
            }}
          />
        </div>
      )}

      <ContractTemplateDialog
        open={dialog != null}
        mode={dialog?.mode ?? 'create'}
        template={dialog?.mode === 'edit' ? dialog.template : undefined}
        isPending={pending}
        onClose={() => setDialog(null)}
        onSubmit={submit}
      />

      <ConfirmDialog
        open={deleteFor != null}
        variant="danger"
        title={t('gest:admin.contracts.delete.title')}
        message={t('gest:admin.contracts.delete.message', { name: deleteFor?.name ?? '' })}
        confirmLabel={t('gest:admin.contracts.delete.confirm')}
        cancelLabel={t('gest:admin.contracts.delete.cancel')}
        isLoading={deleteTpl.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteFor(null)}
      />
    </div>
  );
}
